import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Sparkles, Search, Folder, Loader2, Unlink, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog to link a WhatsApp group to a dossier (or unlink it).
 *
 * Two panels:
 *  1) AI-suggested matches (fuzzy match on group name)
 *  2) Free-text search across all accessible dossiers
 */
export default function LinkGroupToDossierDialog({
  open,
  onOpenChange,
  personId,
  group,           // { group_id, group_name, dossier_id, dossier_title }
  onSaved,         // callback({ dossier_id, dossier_title })
}) {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ dossiers: [], contacts: [] });

  // Fetch the AI suggestions when the dialog opens
  useEffect(() => {
    if (!open || !group?.group_id) return;
    let cancelled = false;
    setLoading(true);
    setSearchTerm('');
    setSearchResults({ dossiers: [], contacts: [] });
    axios.get(
      `${API}/persons/${personId}/sources/whatsapp/groups/${encodeURIComponent(group.group_id)}/suggestions?person_id=${personId}`,
      auth
    )
      .then((res) => { if (!cancelled) setSuggestions(res.data?.suggestions || []); })
      .catch((e) => {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Impossible de charger les suggestions');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group?.group_id, personId]);

  // Debounced server-side combined search (dossiers + contacts) on user input
  useEffect(() => {
    if (!open) return;
    const q = searchTerm.trim();
    if (q.length < 2) {
      setSearchResults({ dossiers: [], contacts: [] });
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      axios.get(
        `${API}/persons/${personId}/sources/whatsapp/groups/${encodeURIComponent(group.group_id)}/search?person_id=${personId}&q=${encodeURIComponent(q)}`,
        auth
      )
        .then((res) => {
          if (!cancelled) setSearchResults({
            dossiers: res.data?.dossiers || [],
            contacts: res.data?.contacts || [],
          });
        })
        .catch((e) => {
          if (!cancelled) toast.error(e.response?.data?.detail || 'Erreur de recherche');
        })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchTerm, group?.group_id, personId]);

  const submitLink = async (payload, optimisticTitle) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/persons/${personId}/sources/whatsapp/groups/${encodeURIComponent(group.group_id)}/link?person_id=${personId}`,
        payload,
        auth
      );
      const newTitle = res.data?.dossier_title ?? optimisticTitle ?? null;
      const newId = res.data?.dossier_id ?? null;
      const reassigned = res.data?.reassigned_memories || 0;
      const created = res.data?.dossier_was_created;
      if (newId) {
        let msg = created
          ? `Dossier « ${newTitle} » créé et lié au groupe`
          : `Groupe lié à « ${newTitle || 'ce dossier'} »`;
        if (reassigned > 0) msg += ` · ${reassigned} mémoire${reassigned > 1 ? 's' : ''} réattribuée${reassigned > 1 ? 's' : ''}`;
        toast.success(msg);
      } else {
        toast.success('Lien supprimé');
      }
      onSaved?.({ dossier_id: newId, dossier_title: newTitle });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la liaison');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkDossier = (dossierId, dossierTitle) =>
    submitLink({ dossier_id: dossierId }, dossierTitle);
  const handleLinkContact = (contactId, contactLabel) =>
    submitLink({ contact_id: contactId }, `Dossier — ${contactLabel}`);
  const handleUnlink = () => submitLink({ dossier_id: null, contact_id: null }, null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="link-group-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-indigo-600" />
            Lier à un dossier
          </DialogTitle>
          <DialogDescription>
            Groupe&nbsp;: <span className="font-medium text-foreground">{group?.group_name || 'sans nom'}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Current link banner */}
        {group?.dossier_id && (
          <div className="flex items-center justify-between gap-2 p-3 rounded-md border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-900">
            <div className="text-sm">
              Actuellement lié à&nbsp;: <span className="font-semibold">{group.dossier_title || group.dossier_id}</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={handleUnlink}
              disabled={saving}
              data-testid="unlink-group-btn"
            >
              <Unlink className="w-3.5 h-3.5 mr-1" /> Délier
            </Button>
          </div>
        )}

        {/* AI suggestions */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Suggestions auto
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Analyse du nom du groupe…
            </div>
          ) : suggestions.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-1">
              Aucune correspondance évidente — utilise la recherche ci-dessous.
            </p>
          ) : (
            <div className="space-y-1">
              {suggestions.map(s => (
                <button
                  key={s.dossier_id}
                  type="button"
                  onClick={() => handleLinkDossier(s.dossier_id, s.title)}
                  disabled={saving || s.dossier_id === group?.dossier_id}
                  className="w-full flex items-center justify-between gap-2 p-2.5 rounded-md border bg-card hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  data-testid={`suggestion-${s.dossier_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{s.title}</p>
                    {s.primary_contact_name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {s.primary_contact_name}
                      </p>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-[10px] shrink-0">
                    {s.score}%
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Free-text search */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Search className="w-3.5 h-3.5" /> Recherche libre
          </div>
          <Input
            placeholder="Nom, société, email… (min. 2 caractères)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="dossier-search-input"
          />
          <div className="max-h-72 overflow-y-auto space-y-2 border rounded-md p-1">
            {searching && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground p-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Recherche…
              </div>
            )}

            {!searching && searchTerm.trim().length >= 2 &&
              searchResults.dossiers.length === 0 && searchResults.contacts.length === 0 && (
              <p className="text-xs text-muted-foreground italic p-2">Aucun résultat</p>
            )}

            {/* Dossiers existants */}
            {searchResults.dossiers.length > 0 && (
              <div className="space-y-1">
                <div className="px-2 pt-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Dossiers existants
                </div>
                {searchResults.dossiers.map(d => (
                  <button
                    key={d.dossier_id}
                    type="button"
                    onClick={() => handleLinkDossier(d.dossier_id, d.title)}
                    disabled={saving || d.dossier_id === group?.dossier_id}
                    className="w-full flex items-center justify-between gap-2 p-2 rounded hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    data-testid={`dossier-row-${d.dossier_id}`}
                  >
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <Folder className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm truncate">{d.title}</p>
                        {d.primary_contact_name && (
                          <p className="text-xs text-muted-foreground truncate">{d.primary_contact_name}</p>
                        )}
                      </div>
                    </div>
                    {d.dossier_id === group?.dossier_id && (
                      <Badge variant="outline" className="text-[10px] shrink-0">déjà lié</Badge>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Contacts sans dossier (créera un dossier au clic) */}
            {searchResults.contacts.length > 0 && (
              <div className="space-y-1">
                <div className="px-2 pt-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                  <UserPlus className="w-3 h-3" /> Contacts — créera un dossier
                </div>
                {searchResults.contacts.map(c => (
                  <button
                    key={c.contact_id}
                    type="button"
                    onClick={() => handleLinkContact(c.contact_id, c.name)}
                    disabled={saving}
                    className="w-full flex items-center justify-between gap-2 p-2 rounded hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    data-testid={`contact-row-${c.contact_id}`}
                  >
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {[c.company, c.email, c.phone].filter(Boolean).join(' · ') || 'Sans détails'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0 border-emerald-300 text-emerald-700 dark:text-emerald-300 dark:border-emerald-800">
                      + Dossier
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="link-dialog-cancel">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
